import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@shadcn/ui/dropdown-menu";
import { createLink, Link } from "@tanstack/react-router";
import { EllipsisVertical, LucideIcon } from "lucide-react";
import { forwardRef, PropsWithChildren } from "react";

interface TabNavLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
	label: string;
	icon?: LucideIcon
}

export const TabNavLink = createLink(
	forwardRef<HTMLAnchorElement, TabNavLinkProps>((props, ref) => {
		const { label, icon, ...linkProps } = props;
		const Icon = icon;

		return (
			<a
				ref={ref}
				{...linkProps}
				className="text-muted-foreground [[data-status=active]]:text-foreground flex-shrink-0 py-1.5 text-sm [[data-status=active]]:border-primary [[data-status=active]]:border-b-2 snap-start"
			>
				<span className="hover:bg-muted inline-flex items-center gap-2 rounded-md px-2.5 py-2 md:px-3">
					{label}
					{Icon ? <Icon className="h-4 w-4" /> : null}
				</span>
			</a>
		);
	})
);

export const TabNavLinks = (props: PropsWithChildren & { className?: string }) => {
	const { children, className } = props;

	return (
		<nav className={cn("border-border border-b", className)}>
			<div className="container mx-auto flex overflow-x-auto font-breit snap-x snap-mandatory scroll-px-3.5">{children}</div>
		</nav>
	);
};

export function PageHeader3() {
	return (
		<div className="border-border bg-background border-b pt-0 pb-4 md:pb-6">
			<nav className="border-border mb-6 border-b">
				<div className="container mx-auto flex overflow-x-auto px-2 lg:px-3.5">
					<Link to="/" className="border-primary text-foreground flex-shrink-0 border-b-2 py-1.5 text-sm">
						<span className="hover:bg-muted block rounded-md px-2.5 py-2 md:px-3">Profile</span>
					</Link>
					<Link to="/" className="text-muted-foreground flex-shrink-0 py-1.5 text-sm">
						<span className="hover:bg-muted block rounded-md px-2.5 py-2 md:px-3">Reports</span>
					</Link>
					<Link to="/" className="text-muted-foreground flex-shrink-0 py-1.5 text-sm">
						<span className="hover:bg-muted block rounded-md px-2.5 py-2 md:px-3">Analytics</span>
					</Link>
					<Link to="/" className="text-muted-foreground flex-shrink-0 py-1.5 text-sm">
						<span className="hover:bg-muted block rounded-md px-2.5 py-2 md:px-3">Notifications</span>
					</Link>
					<Link to="/" className="text-muted-foreground flex-shrink-0 py-1.5 text-sm">
						<span className="hover:bg-muted block rounded-md px-2.5 py-2 md:px-3">Help Center</span>
					</Link>
				</div>
			</nav>
			<div className="container mx-auto flex flex-col gap-6 px-4 lg:px-6">
				{/* Main content */}
				<div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
					<div className="space-y-2">
						<h1 className="text-2xl font-bold tracking-tight md:text-3xl">Project alpha</h1>
						<p className="text-muted-foreground text-sm lg:text-base">
							Manage your project's details such as name, image, description and settings.
						</p>
					</div>
					{/* Buttons */}
					<div className="flex flex-row-reverse justify-end gap-2 md:flex-row">
						<div className="lg:hidden">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline" size="icon">
										<EllipsisVertical className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem>Share</DropdownMenuItem>
									<DropdownMenuItem>View</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
						<Button variant="outline" className="hidden lg:inline-flex">
							Share
						</Button>
						<Button variant="outline" className="hidden lg:inline-flex">
							View
						</Button>
						<Button variant="outline">Edit</Button>
						<Button>Publish</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
